import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import {
  TOPBAR_MOBILE_HEIGHT,
  PATIENT_BOTTOM_MOBILE_HEIGHT,
} from '@/v2/constants'
import MobilePatientRoutes from '@/v2/routes/MobilePatientRoutes'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import PatientBarMobile from '../PatientBar/mobile'
import NavigationTabs from '../NavigationTabs/mobile'
import Typography from '@/v2/components/common/Typography'
import PatientAvatar from '@/v2/components/common/PatientAvatar'
import { usePatient, usePatientProfile } from '@/v2/hooks/patient/usePatient'
import { usePatientOfficeNotes } from '@/v2/hooks/patient/usePatientNotes'
import OverduePaymentWarning from '../OverduePaymentWarning'

const Container = styled.div`
  position: fixed;
  top: ${TOPBAR_MOBILE_HEIGHT};
  left: 0;
  right: 0;
  bottom: ${PATIENT_BOTTOM_MOBILE_HEIGHT};
  width: 100vw;
  display: flex;
  flex-direction: column;
  background: #fff;
  z-index: 50;
  box-sizing: border-box;
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  margin: 0;
`

const PatientHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`

const PatientInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const PatientName = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PatientId = styled(Typography)`
  color: ${({ theme }) => theme?.gray?.dark || '#666'};
`

const MedicalAlertsBadge = styled.div`
  background: ${({ theme }) => theme?.indigo?.medicalAlert || '#3f51b5'};
  color: #fff;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 8px;
  display: inline-flex;
  align-self: flex-start;
`

const TabsWrapper = styled.div`
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  background: #f5f5f5;
`

const Content = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const ErrorWrapper = styled.div`
  text-align: center;
  margin-top: 60px;
`

const RESTRICTED_MOBILE_TABS = []

const MobilePatient = () => {
  const { id, view } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    onSetPatient,
    onSetOfficeNotes,
    patient: contextPatient,
    medicalAlerts,
  } = usePatientContext()
  const { patient, isLoading, isError } = usePatient(id)
  const { patient: profileData } = usePatientProfile(id)
  const {
    notes,
    isLoading: isLoadingNotes,
    isError: isErrorNotes,
  } = usePatientOfficeNotes(id)

  const hasInitialized = useRef(false)
  const lastPatientId = useRef(null)

  if (lastPatientId.current !== id) {
    hasInitialized.current = false
    lastPatientId.current = id
  }

  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      patient &&
      patient.id &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true
      onSetPatient(patient)
    }
  }, [isLoading, isError, patient?.id])

  useEffect(() => {
    if (profileData && patient?.id) {
      onSetPatient({
        id: patient.id,
        dob: profileData.dob,
        gender: profileData.gender,
        ssn: profileData.ssn,
        referredBy: profileData.referredBy,
        maAttestedAt: profileData.maAttestedAt,
        address1: profileData.address1,
        address2: profileData.address2,
        city: profileData.city,
        state: profileData.state,
        country: profileData.country,
        zipcode: profileData.zipcode,
        emergencyName: profileData.emergencyName,
        emergencyRelationship: profileData.emergencyRelationship,
        emergencyEmail: profileData.emergencyEmail,
        emergencyPhoneNumber: profileData.emergencyPhoneNumber,
        pregnant: profileData.pregnant,
        premedication: profileData.premedication,
        latexAllergy: profileData.latexAllergy,
        insuranceForms: profileData.insuranceForms || [],
        contract: profileData.contract,
        intakeForm: profileData.intakeForm,
        conversation: profileData.conversation,
        familyMembers: profileData.familyMembers,
        fakeGfPatientId: profileData.fakeGfPatientId,
      })
    }
  }, [profileData, patient?.id])

  useEffect(() => {
    if (!isLoadingNotes && !isErrorNotes && notes) {
      onSetOfficeNotes(notes)
    }
  }, [isLoadingNotes, isErrorNotes, notes])

  // Redirect to profile if user is on a restricted tab (desktop-only tabs)
  useEffect(() => {
    if (view && RESTRICTED_MOBILE_TABS.includes(view)) {
      navigate(`/v2/patient/${id}/profile`, { replace: true })
    }
  }, [view, id, navigate])

  if (isLoading)
    return (
      <Typography textAlign="center" sx={{ mt: '12px' }}>
        Loading...
      </Typography>
    )
  if (isError) {
    return <ErrorWrapper>Sorry! We couldn't load the page.</ErrorWrapper>
  }

  const displayPatient = contextPatient?.id ? contextPatient : patient

  return (
    <Container>
      <PatientHeader>
        <PatientAvatar patient={displayPatient} size={80} />
        <PatientInfo>
          <PatientName variant="h2" fontSize="18px" fontWeight="600">
            {displayPatient?.fullName || 'Patient'}
          </PatientName>
          <PatientId variant="h5" fontSize="12px" theme={theme.palette}>
            PATIENT ID <strong>#{displayPatient?.id}</strong>
          </PatientId>
          {medicalAlerts?.length > 0 && (
            <MedicalAlertsBadge theme={theme.palette}>
              Medical Alerts ({medicalAlerts.length})
            </MedicalAlertsBadge>
          )}
        </PatientInfo>
      </PatientHeader>

      <TabsWrapper>
        <NavigationTabs />
      </TabsWrapper>

      <Content>
        <OverduePaymentWarning />
        <MobilePatientRoutes />
      </Content>

      <PatientBarMobile />
    </Container>
  )
}

export default MobilePatient
